import React, { useState, useMemo, useCallback } from 'react';
import { ProfessionalContentEntity, ChapterEntity, ReferenceDocumentEntity } from '../../generated/graphql';
import { ChevronDownIcon, ChevronRightIcon, SearchIcon } from '@heroicons/react/outline';

import qf from "../../components/refdoc-item/qf.webp";
import mdf from "../../components/refdoc-item/mdfnew.png";
import classNames from 'classnames';
import { CustomRefdocModal } from '../../screens/custom-refdoc/custom-refdoc-modal';
import { QuickFlowModal } from '../../screens/quickflow/quickflow-modal';

interface StreamsLayoutProps {
  streams: ProfessionalContentEntity[];
  refdocCount?: { [k: string]: number };
  loading?: boolean;
  error?: string;
}

export const StreamsLayout: React.FC<StreamsLayoutProps> = ({
  streams,
  refdocCount = {},
  loading = false,
  error
}) => {
  const [expandedStreams, setExpandedStreams] = useState<Set<number>>(new Set());
  const [selectedChapter, setSelectedChapter] = useState<ChapterEntity | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRefdoc, setSelectedRefdoc] = useState<ReferenceDocumentEntity | null>(null);

  const toggleStream = useCallback((streamId: number) => {
    const newExpanded = new Set(expandedStreams);
    if (newExpanded.has(streamId)) {
      newExpanded.delete(streamId);
    } else {
      newExpanded.add(streamId);
    }
    setExpandedStreams(newExpanded);
  }, [expandedStreams]);

  const handleChapterSelect = useCallback((chapter: ChapterEntity) => {
    setSelectedChapter(chapter);
    setSelectedRefdoc(null);
  }, []);

  const handleKeyDown = useCallback((event: React.KeyboardEvent, action: () => void) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      action();
    }
  }, []);

  // Filter streams and chapters based on search term
  const filteredStreams = useMemo(() => {
    if (!searchTerm.trim()) return streams;

    return streams.map(stream => ({
      ...stream,
      chapters: stream.chapters.filter(chapter =>
        chapter.chapter_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        chapter.refdocs.some(refdoc =>
          refdoc.document_name.toLowerCase().includes(searchTerm.toLowerCase())
        )
      )
    })).filter(stream => stream.chapters.length > 0);
  }, [streams, searchTerm]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-slate-500">
        <div>
          Loading...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 h-screen p-4 bg-slate-50">
        <div className="flex flex-col items-center justify-center h-full">
          <p className="text-red-600 font-medium">Error: {error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-7 gap-4 p-4 bg-slate-50 flex-1 overflow-auto text-base">
      {/* First Column - Streams */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden flex flex-col border border-gray-300 col-span-2">
        <h3 className="px-4 py-4 m-0 bg-slate-100 border-b border-gray-200 text-lg font-semibold text-slate-700">
          Streams
        </h3>

        {/* Search Bar */}
        <div className="p-4 border-b border-gray-200">
          <div className="relative">
            <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              placeholder="Search streams, chapters, or refdocs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md bg-white transition-colors focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 placeholder-slate-400"
              aria-label="Search streams, chapters, or refdocs"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-2">
          {filteredStreams.length === 0 ? (
            <div className="flex items-center justify-center h-full text-slate-500 italic text-center p-8">
              <p>No streams found matching "{searchTerm}"</p>
            </div>
          ) : (
            filteredStreams.map((stream) => (
              <div key={stream.id} className="mb-2">
                <div
                  className="flex items-center p-3 bg-slate-50 rounded-md cursor-pointer transition-all duration-200 border border-gray-200 hover:bg-slate-200 hover:border-gray-300 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10"
                  onClick={() => toggleStream(Number(stream.id))}
                  onKeyDown={(e) => handleKeyDown(e, () => toggleStream(Number(stream.id)))}
                  tabIndex={0}
                  role="button"
                  aria-expanded={expandedStreams.has(Number(stream.id))}
                  aria-label={`${stream.name} stream with ${stream.chapters.length} chapters`}
                >
                  <div className="mr-2 text-slate-500">
                    {expandedStreams.has(Number(stream.id)) ? (
                      <ChevronDownIcon className="w-4 h-4" />
                    ) : (
                      <ChevronRightIcon className="w-4 h-4" />
                    )}
                  </div>
                  <span className="flex-1 font-medium text-slate-700">{stream.name}</span>
                  <span className="text-slate-500 text-sm">({stream.chapters.length})</span>
                </div>

                {expandedStreams.has(Number(stream.id)) && (
                  <div className="ml-6 mt-2">
                    {stream.chapters.map((chapter) => (
                      <div
                        key={chapter.id}
                        className={`flex items-center justify-between p-2 mb-1 rounded cursor-pointer transition-all duration-200 hover:bg-slate-100 focus:outline-none focus:bg-slate-100 focus:ring-2 focus:ring-blue-500/10 ${selectedChapter?.id === chapter.id
                          ? 'bg-blue-500 text-white hover:bg-blue-500 focus:bg-blue-500 focus:ring-blue-500/20'
                          : ''
                          }`}
                        onClick={() => handleChapterSelect(chapter)}
                        onKeyDown={(e) => handleKeyDown(e, () => handleChapterSelect(chapter))}
                        tabIndex={0}
                        role="button"
                        aria-label={`${chapter.chapter_name} chapter with ${chapter.refdocs.length} refdocs`}
                      >
                        <span className="">{chapter.chapter_name}</span>
                        <span className={`text-xs ${selectedChapter?.id === chapter.id ? 'text-blue-100' : 'text-slate-500'
                          }`}>
                          ({chapter.refdocs.length})
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Second Column - Refdocs */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden flex flex-col border border-gray-300 col-span-2">
        <h3 className="px-4 py-4 m-0 bg-slate-100 border-b border-gray-200 text-lg font-semibold text-slate-700">
          {selectedChapter ? `Refdocs - ${selectedChapter.chapter_name}` : 'Refdocs'}
        </h3>
        <div className="flex-1 overflow-y-auto p-2">
          {selectedChapter ? (
            selectedChapter.refdocs.length === 0 ? (
              <div className="flex items-center justify-center h-full text-slate-500 italic text-center p-8">
                <p>No refdocs found in this chapter</p>
              </div>
            ) : (
              selectedChapter.refdocs.map((refdoc) => (
                <div
                  key={refdoc.id}
                  className={classNames(
                    "flex items-center justify-between p-3 mb-2 bg-slate-50 rounded-md border border-gray-200 hover:bg-slate-200 hover:border-gray-300 transition-all duration-200",
                    selectedRefdoc?.id === refdoc.id ? "bg-blue-500 text-white hover:bg-blue-500 focus:bg-blue-500 focus:ring-blue-500/20" : ""
                  )}
                  onClick={() => setSelectedRefdoc(refdoc)}
                  onKeyDown={(e) => handleKeyDown(e, () => setSelectedRefdoc(refdoc))}
                  tabIndex={0}
                  role="button"
                  aria-label={`${refdoc.document_name} refdoc`}
                >
                  <img src={refdoc.reftype === "CUSTOM" ? mdf : qf} alt={refdoc.document_name} className={refdoc.reftype === "QUICKFLOW" ? "h-10 w-10" : "h-8 w-8"} />
                  <div className={classNames(
                    "flex-1",
                    refdoc.reftype === "QUICKFLOW" ? "pl-2" : "pl-4"
                  )}>
                    <span className="block font-medium text-slate-700 mb-1">{refdoc.document_name}</span>
                  </div>
                </div>
              ))
            )
          ) : (
            <div className="flex items-center justify-center h-full text-slate-500 italic text-center p-8 text-base">
              <p>Select a chapter to view refdocs</p>
            </div>
          )}
        </div>
      </div>

      {/* Third Column - Content (placeholder for future) */}
      <div className="bg-white rounded-lg shadow-sm flex flex-col border border-gray-300 overflow-auto col-span-3">
        <h3 className="px-4 py-4 m-0 bg-slate-100 border-b border-gray-200 text-lg font-semibold text-slate-700">
          Content
        </h3>
        <div className="flex-1 flex text-base overflow-auto">
          {selectedRefdoc && selectedRefdoc.reftype === "QUICKFLOW" && (
            <QuickFlowModal refDocId={selectedRefdoc.id} closePreview={() => setSelectedRefdoc(null)} />
          )}
          {selectedRefdoc && selectedRefdoc.reftype === "CUSTOM" && (
            <CustomRefdocModal refDocId={selectedRefdoc.id} closePreview={() => setSelectedRefdoc(null)} />
          )}
        </div>
      </div>
    </div>
  );
}; 